import { definePreset } from '@primeuix/themes';
import Nora from '@primeuix/themes/nora';

export default definePreset(Nora, {
  primitive: {
    yellow: {
      50: '#FDF9E6',
      100: '#FCF1BD',
      200: '#FAE082',
      300: '#F7C743',
      400: '#FFB808',
      500: '#F4B51B',
      600: '#E09210',
      700: '#BD6C0D',
      800: '#924A0E',
      900: '#773B12',
      950: '#3A1603'
    },
    blue: {
      50: '#F3F6F9',
      100: '#F3F6F9',
      200: '#E8EBF1',
      300: '#CCD6E1',
      400: '#A0B4C8',
      500: '#718DAB',
      600: '#517091',
      700: '#3E5777',
      800: '#34465E',
      900: '#2D3C4F',
      950: '#111419'
    },
    black: '#000000'
  },
  semantic: {
    primary: {
      50: '{yellow.50}',
      100: '{yellow.100}',
      200: '{yellow.200}',
      300: '{yellow.300}',
      400: '{yellow.400}',
      500: '{yellow.500}',
      600: '{yellow.600}',
      700: '{yellow.700}',
      800: '{yellow.800}',
      900: '{yellow.900}',
      950: '{yellow.950}'
    },
    colorScheme: {
      light: {
        primary: { color: '{primary.400}' },
        surface: {
          0: '#ffffff',
          50: '{blue.50}',
          100: '{blue.100}',
          200: '{blue.200}',
          300: '{blue.300}',
          400: '{blue.400}',
          500: '{blue.500}',
          600: '{blue.600}',
          700: '{blue.700}',
          800: '{blue.800}',
          900: '{blue.900}',
          950: '{blue.950}'
        }
      }
    }
  },
  components: {
    // Nora renders tags at 700, which reads heavier than anything else in this theme — button labels
    // are already brought down to 400, and headings sit at 500/600.
    tag: {
      root: {
        fontWeight: '500'
      }
    },
    button: {
      root: {
        label: {
          fontWeight: '400'
        },
        paddingX: '16px',
        paddingY: '8px',
        sm: {
          paddingX: '16px',
          paddingY: '8px'
        },
        borderRadius: '2px'
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              color: '{blue.900}',
              hoverColor: '{yellow.900}',
              hoverBorderColor: '{yellow.500}',
              hoverBackground: '{yellow.500}'
            },
            secondary: {
              color: '{black}',
              hoverColor: '{black}',
              borderColor: '{blue.200}',
              hoverBorderColor: '{blue.100}',
              hoverBackground: '{blue.100}'
            }
          },
          outlined: {
            secondary: {
              color: '{black}',
              borderColor: '{blue.200}'
            },
            primary: {
              color: '{yellow.700}',
              borderColor: '{yellow.700}'
            }
          }
        }
      }
    },
    overlaybadge: {
      root: {
        outline: {
          width: '0px'
        }
      }
    },
    badge: {
      colorScheme: {
        light: {
          primary: {
            color: '{black}'
          }
        }
      },
      root: {
        height: '12px',
        minWidth: '12px',
        fontSize: '9px'
      }
    },
    inputtext: {
      root: {
        borderRadius: '2px',
        placeholderColor: '{black}',
        borderColor: '{blue.300}',
        hoverBorderColor: '{blue.400}',
        focusRing: {
          width: '1px'
        }
      }
    },
    select: {
      root: {
        borderRadius: '2px',
        placeholderColor: '{black}',
        borderColor: '{blue.300}',
        hoverBorderColor: '{blue.400}',
        disabledBackground: '{blue.200}',
        disabledColor: '{blue.900}'
      },
      option: {
        padding: '4px'
      }
    },
    multiselect: {
      root: {
        borderColor: '{blue.300}',
        hoverBorderColor: '{blue.400}'
      }
    },
    tree: {
      root: {
        indent: '8px'
      },
      node: {
        gap: '0px',
        selectedBackground: 'transparent',
        hoverColor: '{yellow.500}',
        hoverBackground: 'transparent',
        selectedColor: '{yellow.700}',
        padding: '2px'
      },
      nodeToggleButton: {
        hoverBackground: 'transparent',
        selectedHoverBackground: 'transparent'
      }
    },
    toggleswitch: {
      root: {
        height: '16px',
        width: '26px'
      },
      handle: {
        size: '10px'
      }
    },
    checkbox: {
      root: {
        checkedBackground: '{yellow.400}',
        checkedBorderColor: '{yellow.200}',
        checkedHoverBackground: '{yellow.300}',
        checkedHoverBorderColor: '{yellow.200}',
        width: '16px',
        height: '16px',
        background: '{blue.200}',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent'
      }
    },
    popover: {
      root: {
        borderColor: '{blue.300}',
        borderRadius: '4px',
        arrowOffset: '-15px'
      },
      content: {
        padding: '0px'
      }
    },
    tabs: {
      tablist: {
        borderWidth: '1px 0 1px 0',
        borderColor: '{blue.300}'
      },
      activeBar: {
        height: '2px',
        bottom: '-1px',
        background: '{yellow.500}'
      },
      tabpanel: {
        background: '{yellow.100}',
        padding: '6px'
      },
      tab: {
        activeColor: '{yellow.500}',
        activeBackground: '{white}',
        color: '{blue.500}',
        fontWeight: '600'
      }
    },
    contextmenu: {
      root: {
        borderRadius: '6px',
        borderColor: '{blue.100}'
      },
      list: {
        padding: '8px'
      },
      item: {
        focusBackground: '{yellow.50}',
        focusColor: '{blue.800}',
        gap: '12px',
        padding: '10px'
      },
      separator: {
        borderColor: '{blue.200}'
      }
    },
    dialog: {
      title: {
        fontSize: '16px',
        fontWeight: '500'
      },
      header: {
        padding: '0px'
      },
      content: {
        padding: '0px'
      }
    },
    message: {
      text: {
        fontWeight: '400',
        fontSize: '14px'
      }
    },
    listbox: {
      option: {
        selectedColor: 'black',
        selectedBackground: '{yellow.50}',
        selectedFocusColor: 'black',
        selectedFocusBackground: '{yellow.50}',
        focusBackground: '{blue.100}'
      }
    },
    autocomplete: {
      dropdown: {
        borderColor: '{blue.300}',
        hoverBorderColor: '{blue.400}'
      }
    },
    confirmpopup: {
      root: {
        borderColor: '#e2e8f0',
        arrowOffset: '0.75rem'
      }
    },
    datatable: {
      row: {
        color: '{blue.950}',
        selectedColor: '{blue.950}',
        selectedBackground: '{yellow.50}',
        focusRing: {
          color: 'transparent'
        }
      }
    },
    toast: {
      root: {
        borderRadius: '4px'
      },
      content: {
        padding: '12px 16px'
      },
      colorScheme: {
        light: {
          info: {
            background: '#ffffff',
            borderColor: '{blue.400}',
            color: '{blue.800}',
            detailColor: '{blue.600}',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          },
          success: {
            background: '#ffffff',
            borderColor: '#6B9E78',
            color: '{blue.800}',
            detailColor: '{blue.600}',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          },
          warn: {
            background: '#ffffff',
            borderColor: '#D4915E',
            color: '{blue.800}',
            detailColor: '{blue.600}',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          },
          error: {
            background: '#ffffff',
            borderColor: '#C46B6B',
            color: '{blue.800}',
            detailColor: '{blue.600}',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }
        }
      }
    }
  }
});
